import { useEffect, useState } from "react";
import {
  DEFAULT_NOTIFICATION_SETTINGS,
  NOTIFICATION_SETTINGS_CHANGED_EVENT,
  NOTIFICATION_SETTINGS_STORAGE_KEY,
} from "../../push/pushConstants";
import {
  fetchMyNotificationSettings,
  updateMyNotificationSettings,
} from "../../api/notification/notificationSettingsApi";
import useAutoClearMessage from "../../hooks/useAutoClearMessage";

const APP_VERSION = process.env.REACT_APP_APP_VERSION || "1.0.0";
const TERMS_URL = process.env.REACT_APP_TERMS_URL || "/terms";
const PRIVACY_URL = process.env.REACT_APP_PRIVACY_URL || "/privacy";
const MARKETING_CONSENT_URL =
  process.env.REACT_APP_MARKETING_CONSENT_URL || "/marketing-consent";

function loadNotificationSettings() {
  const rawValue = localStorage.getItem(NOTIFICATION_SETTINGS_STORAGE_KEY);
  if (!rawValue) {
    return { ...DEFAULT_NOTIFICATION_SETTINGS };
  }

  try {
    const parsed = JSON.parse(rawValue);
    return {
      ...DEFAULT_NOTIFICATION_SETTINGS,
      ...parsed,
    };
  } catch (error) {
    return { ...DEFAULT_NOTIFICATION_SETTINGS };
  }
}

function normalizeNotificationSettings(source) {
  const raw = source?.data || source?.settings || source || {};
  return {
    ...DEFAULT_NOTIFICATION_SETTINGS,
    pushEnabled:
      raw.pushEnabled ?? raw.push ?? DEFAULT_NOTIFICATION_SETTINGS.pushEnabled,
    scheduleNoticeEnabled:
      raw.scheduleNoticeEnabled ??
      raw.scheduleNotice ??
      raw.schedule ??
      DEFAULT_NOTIFICATION_SETTINGS.scheduleNoticeEnabled,
    voteNoticeEnabled:
      raw.voteNoticeEnabled ??
      raw.voteNotice ??
      raw.vote ??
      DEFAULT_NOTIFICATION_SETTINGS.voteNoticeEnabled,
    joinRequestNoticeEnabled:
      raw.joinRequestNoticeEnabled ??
      raw.joinRequestNotice ??
      raw.joinRequest ??
      DEFAULT_NOTIFICATION_SETTINGS.joinRequestNoticeEnabled,
  };
}

function saveNotificationSettings(nextSettings) {
  localStorage.setItem(
    NOTIFICATION_SETTINGS_STORAGE_KEY,
    JSON.stringify(nextSettings),
  );
  window.dispatchEvent(
    new CustomEvent(NOTIFICATION_SETTINGS_CHANGED_EVENT, {
      detail: nextSettings,
    }),
  );
}

function NotificationToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled = false,
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-xl border border-[#eaecf0] bg-white px-4 py-3">
      <div>
        <p className="m-0 text-sm font-semibold text-[#101828]">{label}</p>
        <p className="mt-1 text-xs text-[#667085]">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={onChange}
        className={`inline-flex h-7 w-12 shrink-0 items-center rounded-full p-1 transition-colors duration-200 ${
          checked ? "bg-[#111827]" : "bg-[#d0d5dd]"
        } disabled:cursor-not-allowed`}
      >
        <span
          className={`h-5 w-5 rounded-full bg-white shadow-[0_1px_2px_rgba(16,24,40,0.2)] transition-transform duration-200 ${
            checked ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </button>
    </div>
  );
}

function AppInfoRow({ label, value, href }) {
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="flex items-center justify-between rounded-xl border border-[#eaecf0] bg-white px-4 py-3 text-sm font-semibold text-[#344054] hover:bg-[#f8fafc]"
      >
        <span>{label}</span>
        <span className="text-xs text-[#667085]">열기</span>
      </a>
    );
  }

  return (
    <div className="flex items-center justify-between rounded-xl border border-[#eaecf0] bg-white px-4 py-3">
      <span className="text-sm font-semibold text-[#344054]">{label}</span>
      <span className="text-sm font-semibold text-[#101828]">{value}</span>
    </div>
  );
}

function ClientSettingsPage() {
  const [notificationSettings, setNotificationSettings] = useState(() =>
    loadNotificationSettings(),
  );
  const [isSettingsLoading, setIsSettingsLoading] = useState(true);
  const [isSettingsSaving, setIsSettingsSaving] = useState(false);
  const [settingsErrorMessage, setSettingsErrorMessage] = useState("");
  useAutoClearMessage(settingsErrorMessage, setSettingsErrorMessage);

  const isPushEnabled = notificationSettings.pushEnabled;

  useEffect(() => {
    let isUnmounted = false;

    const loadFromServer = async () => {
      setIsSettingsLoading(true);
      setSettingsErrorMessage("");
      try {
        const responseData = await fetchMyNotificationSettings();
        const serverSettings = normalizeNotificationSettings(responseData);
        if (isUnmounted) {
          return;
        }
        setNotificationSettings(serverSettings);
        saveNotificationSettings(serverSettings);
      } catch (error) {
        if (isUnmounted) {
          return;
        }
        setSettingsErrorMessage(
          "서버 알림 설정을 불러오지 못했습니다. 현재는 저장된 설정으로 표시합니다.",
        );
        const fallbackSettings = loadNotificationSettings();
        setNotificationSettings(fallbackSettings);
        saveNotificationSettings(fallbackSettings);
      } finally {
        if (!isUnmounted) {
          setIsSettingsLoading(false);
        }
      }
    };

    loadFromServer();

    return () => {
      isUnmounted = true;
    };
  }, []);

  const updateSettings = async (patch) => {
    if (isSettingsSaving) {
      return;
    }

    const previousSettings = notificationSettings;
    const optimisticSettings = normalizeNotificationSettings({
      ...previousSettings,
      ...patch,
    });
    setNotificationSettings(optimisticSettings);
    setIsSettingsSaving(true);
    setSettingsErrorMessage("");

    try {
      const responseData =
        await updateMyNotificationSettings(optimisticSettings);
      const savedSettings = normalizeNotificationSettings(
        responseData || optimisticSettings,
      );
      setNotificationSettings(savedSettings);
      saveNotificationSettings(savedSettings);
    } catch (error) {
      setNotificationSettings(previousSettings);
      setSettingsErrorMessage(
        error?.message ||
          "알림 설정 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.",
      );
    } finally {
      setIsSettingsSaving(false);
    }
  };

  return (
    <main
      className="min-h-screen px-6 py-8 md:px-10 md:py-10"
      style={{
        background:
          "radial-gradient(circle at 8% 0%, #dcfce7 0, transparent 30%), radial-gradient(circle at 100% 10%, #dbeafe 0, transparent 34%), linear-gradient(160deg, #f8fafc 0%, #eef2ff 100%)",
      }}
    >
      <div className="mx-auto flex w-full max-w-[980px] flex-col gap-6">
        <section className="rounded-3xl border border-[#dbe4f4] bg-white/90 p-6 shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
          <p className="m-0 inline-block rounded-full bg-[#111827] px-3 py-1 text-xs font-bold tracking-[0.08em] text-white">
            SETTINGS
          </p>
          <h1 className="mt-3 text-[clamp(24px,4.5vw,34px)] font-semibold leading-tight tracking-[-0.03em] text-[#0f172a]">
            설정
          </h1>
          <p className="mt-2 text-sm text-[#475467]">
            알림 옵션과 앱 정보를 관리합니다.
          </p>
        </section>

        <section className="rounded-3xl border border-[#dbe4f4] bg-white/90 p-6 shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="m-0 text-lg font-semibold text-[#0f172a]">알림</h2>
            </div>
          </div>

          {settingsErrorMessage && (
            <p className="mt-3 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-xs text-[#b42318]">
              {settingsErrorMessage}
            </p>
          )}

          <div className="mt-4 space-y-3">
            <NotificationToggleRow
              label="푸시 알림"
              description="일정, 투표, 가입 신청 관련 알림을 전체 제어합니다."
              checked={isPushEnabled}
              disabled={isSettingsLoading || isSettingsSaving}
              onChange={() =>
                updateSettings({
                  pushEnabled: !isPushEnabled,
                })
              }
            />
            <NotificationToggleRow
              label="일정 알림"
              description="팀 일정 등록/변경 시 알림을 받습니다."
              checked={notificationSettings.scheduleNoticeEnabled}
              disabled={!isPushEnabled || isSettingsLoading || isSettingsSaving}
              onChange={() =>
                updateSettings({
                  scheduleNoticeEnabled:
                    !notificationSettings.scheduleNoticeEnabled,
                })
              }
            />
            <NotificationToggleRow
              label="투표 알림"
              description="참여/불참 투표 시작 및 마감 알림을 받습니다."
              checked={notificationSettings.voteNoticeEnabled}
              disabled={!isPushEnabled || isSettingsLoading || isSettingsSaving}
              onChange={() =>
                updateSettings({
                  voteNoticeEnabled: !notificationSettings.voteNoticeEnabled,
                })
              }
            />
            {/* <NotificationToggleRow
              label="가입 신청 알림"
              description="내 팀 가입 신청 상태 변경 알림을 받습니다."
              checked={notificationSettings.joinRequestNoticeEnabled}
              disabled={!isPushEnabled || isSettingsLoading || isSettingsSaving}
              onChange={() =>
                updateSettings({
                  joinRequestNoticeEnabled:
                    !notificationSettings.joinRequestNoticeEnabled,
                })
              }
            /> */}
          </div>
        </section>

        <section className="rounded-3xl border border-[#dbe4f4] bg-white/90 p-6 shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
          <h2 className="m-0 text-lg font-semibold text-[#0f172a]">앱 정보</h2>
          <div className="mt-4 space-y-3">
            <AppInfoRow label="앱 버전" value={APP_VERSION} />
            <AppInfoRow label="이용약관" href={TERMS_URL} />
            <AppInfoRow label="개인정보 처리방침" href={PRIVACY_URL} />
            <AppInfoRow label="마케팅 수신 정책" href={MARKETING_CONSENT_URL} />
          </div>
        </section>
      </div>
    </main>
  );
}

export default ClientSettingsPage;
